import type { Event as SentryEvent } from '@sentry/nextjs';

type ErrorSampleRates = {
  ignoredErrors: {
    patterns: RegExp[];
    sampleRate: number;
  };
  statusCodes: {
    [statusCode: number]: number;
  };
  defaultSampleRate: number;
};

export const ERROR_SAMPLE_RATES: ErrorSampleRates = {
  ignoredErrors: {
    patterns: [
      /Braze Error: Failed to load.*appboycdn\.com\/web-sdk/,
      /GraphQLError.*Content does not exist/i,
      /Error: script timeout.*bazaarvoice\.com/,
      /could not load.*bazaarvoice\.com/,
      /TypeError: Failed to fetch/i,
      /pinterest\.com/i,
    ],
    sampleRate: 0.001,
  },

  statusCodes: {
    404: 0.01,
    429: 0.05,
  },

  defaultSampleRate: 1.0,
};

/**
 * Determine the sample rate to use for a given Sentry event
 * @param event The Sentry event object
 * @returns A number between 0 and 1 representing the sample rate
 */
export function getSampleRateForEvent(event: SentryEvent): number {
  const errorMessage = event?.exception?.values?.[0]?.value ?? '';
  const statusCode = event?.contexts?.response?.status_code;

  for (const pattern of ERROR_SAMPLE_RATES.ignoredErrors.patterns) {
    if (pattern.test(errorMessage)) {
      return ERROR_SAMPLE_RATES.ignoredErrors.sampleRate;
    }
  }

  if (statusCode && ERROR_SAMPLE_RATES.statusCodes[statusCode]) {
    return ERROR_SAMPLE_RATES.statusCodes[statusCode];
  }

  return ERROR_SAMPLE_RATES.defaultSampleRate;
}

/**
 * Apply sampling to a Sentry event
 * @param event The Sentry event object
 * @returns The event if it should be sent, or null if filtered out
 */
export function applySampling(event: SentryEvent): SentryEvent | null {
  const sampleRate = getSampleRateForEvent(event);
  return Math.random() < sampleRate ? event : null;
}
